import type { FieldMapping } from '../../../../types/extraction';

/**
 * Auto-map extracted column headers to standard field names.
 * Uses exact matching logic from mobile app for consistency.
 * Mapping of standard fields to common header variations (all lowercase for case-insensitive matching)
 */
/**
 * Header spellings too generic to beat a specific one. A bare "Team" column on a roster that also
 * has "Team #" is usually the travel team, so it only claims a field once every specific header
 * has had its turn; otherwise, in column order, it would take team_number from the real one.
 */
const WEAK_ALIASES: ReadonlySet<string> = new Set(['team']);

export function autoMapColumns(headers: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const usedFields = new Set<string>();

  const fieldMappings: Record<string, string[]> = {
    // Core fields
    first_name: ['first', 'first name', 'first_name', 'first-name', 'fname', 'given name'],
    last_name: ['last', 'last name', 'last_name', 'last-name', 'lname', 'surname'],
    number: ['#', 'no', 'no.', 'number', 'jersey', 'uni', 'jersey number', 'jersey_number'],
    // Kept in sync with mobile field-auto-mapper.ts and the API's RosterSchoolColumn. No
    // team/organization/club: the school is stored on the prospect, and a travel team's name is
    // not a school. Team maps to team_number below; Organization and Club stay unmapped.
    school: ['school', 'school name', 'school_name', 'high school', 'high_school',
      'high school name', 'high_school_name', 'hs', 'hs name', 'university', 'university name',
      'university_name', 'college', 'college name', 'college_name', 'current school',
      'current_school'],
    position: ['pos', 'position', 'positions'],
    class: ['class', 'cl', 'cl.', 'year', 'grade', 'yr', 'yr.', 'grad year', 'eligibility',
      'elig', 'grad', 'grad yr', 'grad_year', 'grad_yr', 'class year', 'class_year',
      'academic year', 'yog'],

    // Physical fields
    bats: ['bats', 'bat', 'batting', 'b'],
    throws: ['throws', 'throw', 'throwing', 't', 'thr'],
    height: ['ht', 'ht.', 'height'],
    weight: ['wt', 'wt.', 'weight', 'lbs'],
    birth_date: ['dob', 'birthdate', 'birth date', 'birthday', 'birth_date', 'date of birth'],

    // Contact fields
    phone: ['phone', 'cell', 'mobile', 'tel', 'telephone', 'player phone'],
    email: ['email', 'e-mail', 'email address', 'e-mail address', 'player email'],
    twitter: ['twitter', 'twitter handle', 'x', 'x handle', '@'],
    instagram: ['instagram', 'insta', 'ig'],

    // Location fields
    city: ['city', 'hometown'],
    state: ['state', 'st'],
    address: ['address', 'addr', 'street', 'street address', 'street_address', 'street address 1',
      'address 1', 'address1', 'mailing address', 'mailing_address', 'home address', 'home_address'],
    zip: ['zip', 'zipcode', 'zip code', 'zip_code', 'postal', 'postal code', 'postal_code', 'postcode'],

    // Parent/Guardian fields
    parent_name: ['parent', 'parent name', 'guardian', 'guardian name', 'parent/guardian', 'parent_name'],
    parent_phone: ['parent phone', 'parent cell', 'guardian phone', 'parent_phone'],

    // Coach fields
    coach_name: ['coach', 'coach name', 'head coach', 'coach_name'],
    coach_phone: ['coach phone', 'coach cell', 'coach_phone'],

    // Advisor fields
    advisor_name: ['advisor', 'advisor name', 'advisor_name'],
    advisor_phone: ['advisor phone', 'advisor_phone'],

    // Team fields (showcase only)
    team_color: ['c', 'color', 'team color', 'team_color', 'team-color'],
    team_number: ['t', 'team', 'team number', 'team_number', 'team-number', 'team #', 'team_id'],

    // Other fields
    notes: ['notes', 'note', 'comments', 'comment', 'remarks', 'remark'],
  };

  const tryMap = (header: string, allowWeak: boolean) => {
    const normalized = header.toLowerCase().trim();
    if (!allowWeak && WEAK_ALIASES.has(normalized)) return;

    // Try to find matching standard field using EXACT match only
    for (const [targetField, variations] of Object.entries(fieldMappings)) {
      // Skip if this field was already mapped to another column
      if (usedFields.has(targetField)) {
        continue;
      }

      if (variations.includes(normalized)) {
        mapping[header] = targetField;
        usedFields.add(targetField);
        return;
      }
    }

    // No match found - leave unmapped (empty string means skip column)
  };

  headers.forEach((header) => tryMap(header, false));
  headers.filter((header) => !(header in mapping)).forEach((header) => tryMap(header, true));

  return mapping;
}
